import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ShopBottomNav from '../../components/ShopBottomNav';
import {
  getSubscription,
  getTokenBalance,
  getSubscriptionInfo,
  cancelUnlimited,
  applySubscriptionFallbackIfDue,
  endOfBillingMonth,
  refreshTokenBalance,
  applyPlanFromBackend,
} from '../../services/shopState';
import {
  getTokenPackages,
  cancelMySubscription,
  FALLBACK_PACKAGES,
  normalizePackage,
} from '../../services/apiService';
import './ShopTokensPage.css';

const PLAN_LABELS = {
  unlimited: 'Unlimited',
  growth: 'Growth',
  starter: 'Starter',
};

function formatNlDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('nl-NL', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

const eur = (cents) => (Math.round(cents) / 100).toFixed(2).replace('.', ',');
const perToken = (p) => (typeof p?.tokens === 'number' && p.tokens > 0
  ? eur(p.priceExCents / p.tokens) : '0,00');

// Prices + token counts come from the database (token_packages, managed in
// Admin → Pakketten). The 0.2 values are only the fallback.
const FALLBACK_BY_CODE = Object.fromEntries(
  FALLBACK_PACKAGES.map(normalizePackage).map((p) => [p.id, p])
);

export default function ShopTokensPage() {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(getSubscription());
  const [balance, setBalance] = useState(getTokenBalance());
  const [subInfo, setSubInfo] = useState(getSubscriptionInfo());
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelToast, setCancelToast] = useState('');
  const [pkgs, setPkgs] = useState(FALLBACK_BY_CODE);

  useEffect(() => {
    let cancelled = false;
    getTokenPackages().then(({ packages }) => {
      if (!cancelled) setPkgs({ ...FALLBACK_BY_CODE, ...Object.fromEntries(packages.map((p) => [p.id, p])) });
    });
    return () => { cancelled = true; };
  }, []);
  // Packages an admin added beyond the standard three (Admin → Pakketten).
  const extraPkgs = Object.values(pkgs).filter(
    (p) => !['unlimited', 'growth', 'starter'].includes(p.id) && p.type === 'tokens' && p.active
  ).sort((a, b) => a.sortOrder - b.sortOrder);
  const U = pkgs.unlimited;
  const G = pkgs.growth;
  const S = pkgs.starter;

  useEffect(() => {
    const refresh = () => {
      // If a cancelled plan has run out, this flips the shop back to tokens
      // before anything is rendered.
      applySubscriptionFallbackIfDue();
      // See ShopDashboardPage: fired on every refresh so focus and the
      // token-change event pull from the backend rather than re-rendering
      // the stale session value. Settles because the event it may fire is
      // conditional on a value having changed.
      refreshTokenBalance();
      setSubscription(getSubscription());
      setBalance(getTokenBalance());
      setSubInfo(getSubscriptionInfo());
    };
    refresh();
    window.addEventListener('walkin-token-change', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('walkin-token-change', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  const goToCheckout = (pkgId) => {
    navigate(`/shop/checkout?package=${pkgId}`);
  };

  // Cancellation is retailer-initiated only; admin can see it but not change it.
  const handleConfirmCancel = async () => {
    // Backend 0.3 records the cancellation in retailer_subscriptions; an older
    // backend has no endpoint, and the browser-only cancellation is used.
    let next = null;
    try {
      const r = await cancelMySubscription();
      if (applyPlanFromBackend(r)) next = getSubscriptionInfo();
    } catch (err) {
      if (err?.status && err.status !== 404) {
        setConfirmCancel(false);
        setCancelToast(err.message || 'Opzeggen mislukt — probeer het later opnieuw');
        setTimeout(() => setCancelToast(''), 4000);
        return;
      }
    }
    if (!next) next = cancelUnlimited();
    setSubInfo(next);
    setConfirmCancel(false);
    setCancelToast(
      `Abonnement opgezegd — actief tot ${formatNlDate(next.endDate)}`
    );
    setTimeout(() => setCancelToast(''), 4000);
  };

  const plannedEnd = subInfo.endDate || null;

  return (
    <div className="shop-tokens">
      <header className="shop-tokens__header">
        <button
          className="shop-tokens__back"
          onClick={() => navigate(-1)}
          aria-label="Terug"
        >
          ←
        </button>
        <h1 className="shop-tokens__title">Tokens kopen</h1>
        <span className="shop-tokens__spacer" />
      </header>

      <main className="shop-tokens__main">
        <section className="shop-tokens__plan">
          <span className="shop-tokens__plan-label">Huidig plan:</span>
          {subscription === 'unlimited' || balance.unlimited ? (
            <span className="shop-tokens__plan-pill shop-tokens__plan-pill--unlimited">
              ✅ Unlimited actief
            </span>
          ) : (
            <span className="shop-tokens__plan-pill shop-tokens__plan-pill--tokens">
              🎫 {balance.available} {balance.available === 1 ? 'token' : 'tokens'} beschikbaar
            </span>
          )}
        </section>

        {subInfo.isUnlimited && (
          <section className="shop-tokens__sub">
            <div className="shop-tokens__sub-label">Abonnement</div>

            {subInfo.isCancelled ? (
              <>
                <div className="shop-tokens__sub-title">
                  Abonnement opgezegd — actief tot {formatNlDate(plannedEnd)}
                </div>
                <p className="shop-tokens__sub-note">
                  Na {formatNlDate(plannedEnd)} worden deals verrekend via tokens
                  {subInfo.fallbackPlan
                    ? ` (${PLAN_LABELS[subInfo.fallbackPlan] || subInfo.fallbackPlan})`
                    : ''}.
                </p>
              </>
            ) : (
              <>
                <div className="shop-tokens__sub-title">
                  Unlimited abonnement — actief
                </div>
                <p className="shop-tokens__sub-note">
                  Gestart op: {formatNlDate(subInfo.startDate)}
                </p>
                <button
                  type="button"
                  className="shop-tokens__sub-cancel"
                  onClick={() => setConfirmCancel(true)}
                >
                  Abonnement opzeggen
                </button>
              </>
            )}
          </section>
        )}

        {cancelToast && (
          <div className="shop-tokens__sub-toast">{cancelToast}</div>
        )}

        <section className="shop-tokens__info">
          <span className="shop-tokens__info-icon">💡</span>
          <p className="shop-tokens__info-text">
            <strong>Waarom Unlimited?</strong><br />
            Met Unlimited betaal je €{U.priceEx} ex BTW per maand — onbeperkt deals,
            nooit meer nadenken.
          </p>
        </section>

        <section className="shop-tokens__card shop-tokens__card--unlimited">
          <div className="shop-tokens__badges">
            <span className="shop-tokens__badge">🏆 Meest gekozen</span>
            <span className="shop-tokens__badge">💡 Best value</span>
          </div>

          <h2 className="shop-tokens__card-name">Unlimited</h2>
          <p className="shop-tokens__card-subtitle">
            De slimste keuze voor serieuze winkeliers
          </p>

          <div className="shop-tokens__card-tokens">
            <span className="shop-tokens__card-tokens-value">Onbeperkt</span>
            <span className="shop-tokens__card-tokens-label">deals</span>
          </div>

          <div className="shop-tokens__card-price">
            <span className="shop-tokens__card-price-value">€{U.priceEx}</span>
            <span className="shop-tokens__card-price-period">ex BTW / maand</span>
          </div>
          <div className="shop-tokens__card-incl">€{U.priceIncl} incl. {U.vatRate}% BTW</div>
          <div className="shop-tokens__card-pertoken">€0,00 — onbeperkt</div>

          <ul className="shop-tokens__features">
            <li><span className="shop-tokens__check">✅</span> Onbeperkt deals aanmaken</li>
            <li><span className="shop-tokens__check">✅</span> Nooit tokens bijkopen</li>
            <li><span className="shop-tokens__check">✅</span> Opzegbaar per maand</li>
            <li><span className="shop-tokens__check">✅</span> Prioriteitsondersteuning</li>
          </ul>

          <p className="shop-tokens__killer">
            €{U.priceEx} ex BTW per maand — onbeperkt deals,<br />
            nooit meer nadenken.
          </p>

          <button
            className="shop-tokens__cta shop-tokens__cta--pulse"
            onClick={() => goToCheckout('unlimited')}
          >
            Start Unlimited — €{U.priceEx} ex BTW →
          </button>
        </section>

        <div className="shop-tokens__divider">
          <span>Of kies losse tokens:</span>
        </div>

        <section className="shop-tokens__card shop-tokens__card--growth">
          <h2 className="shop-tokens__card-name">Growth</h2>
          <p className="shop-tokens__card-subtitle">
            Voor winkeliers die beginnen
          </p>

          <div className="shop-tokens__card-tokens shop-tokens__card-tokens--small">
            <span className="shop-tokens__card-tokens-value">{G.tokens}</span>
            <span className="shop-tokens__card-tokens-label">tokens</span>
          </div>

          <div className="shop-tokens__card-price shop-tokens__card-price--orange">
            <span className="shop-tokens__card-price-value">€{G.priceEx}</span>
            <span className="shop-tokens__card-price-period">ex BTW</span>
          </div>
          <div className="shop-tokens__card-incl">€{G.priceIncl} incl. {G.vatRate}% BTW</div>
          <div className="shop-tokens__card-pertoken">€{perToken(G)} per token ex BTW</div>

          <p className="shop-tokens__warning">
            Tokens raken op — bijkopen nodig bij drukke periodes
          </p>

          <button
            className="shop-tokens__btn shop-tokens__btn--outline"
            onClick={() => goToCheckout('growth')}
          >
            Kies Growth
          </button>
        </section>

        <section className="shop-tokens__card shop-tokens__card--starter">
          <h2 className="shop-tokens__card-name">Starter</h2>
          <p className="shop-tokens__card-subtitle">Probeer WalkIn uit</p>

          <div className="shop-tokens__card-tokens shop-tokens__card-tokens--small">
            <span className="shop-tokens__card-tokens-value">{S.tokens}</span>
            <span className="shop-tokens__card-tokens-label">tokens</span>
          </div>

          <div className="shop-tokens__card-price shop-tokens__card-price--muted">
            <span className="shop-tokens__card-price-value">€{S.priceEx}</span>
            <span className="shop-tokens__card-price-period">ex BTW</span>
          </div>
          <div className="shop-tokens__card-incl">€{S.priceIncl} incl. {S.vatRate}% BTW</div>
          <div className="shop-tokens__card-pertoken">€{perToken(S)} per token ex BTW</div>

          <p className="shop-tokens__note">Eenmalig — snel op</p>
          <p className="shop-tokens__note">Ideaal om WalkIn te proberen</p>

          <button
            className="shop-tokens__btn shop-tokens__btn--gray"
            onClick={() => goToCheckout('starter')}
          >
            Kies Starter
          </button>
        </section>

        {extraPkgs.map((p) => (
          <section key={p.id} className="shop-tokens__card shop-tokens__card--starter">
            <h2 className="shop-tokens__card-name">{p.name}</h2>
            {p.description && <p className="shop-tokens__card-subtitle">{p.description}</p>}
            <div className="shop-tokens__card-tokens shop-tokens__card-tokens--small">
              <span className="shop-tokens__card-tokens-value">{p.tokens}</span>
              <span className="shop-tokens__card-tokens-label">tokens</span>
            </div>
            <div className="shop-tokens__card-price shop-tokens__card-price--muted">
              <span className="shop-tokens__card-price-value">€{p.priceEx}</span>
              <span className="shop-tokens__card-price-period">ex BTW</span>
            </div>
            <div className="shop-tokens__card-incl">€{p.priceIncl} incl. {p.vatRate}% BTW</div>
            <div className="shop-tokens__card-pertoken">€{perToken(p)} per token ex BTW</div>
            <button
              className="shop-tokens__btn shop-tokens__btn--gray"
              onClick={() => goToCheckout(p.id)}
            >
              Kies {p.name}
            </button>
          </section>
        ))}

        <section className="shop-tokens__compare">
          <h2 className="shop-tokens__compare-title">Vergelijking</h2>
          <div className="shop-tokens__compare-wrap">
            <table className="shop-tokens__table">
              <thead>
                <tr>
                  <th></th>
                  <th>Starter</th>
                  <th>Growth</th>
                  <th className="shop-tokens__col-unlimited">Unlimited</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Aantal deals</td>
                  <td>{S.tokens}</td>
                  <td>{G.tokens}</td>
                  <td className="shop-tokens__col-unlimited">∞</td>
                </tr>
                <tr>
                  <td>Prijs per deal (ex BTW)</td>
                  <td>€{perToken(S)}</td>
                  <td>€{perToken(G)}</td>
                  <td className="shop-tokens__col-unlimited">€0,00</td>
                </tr>
                <tr>
                  <td>Vervalt nooit</td>
                  <td>✅</td>
                  <td>✅</td>
                  <td className="shop-tokens__col-unlimited">✅</td>
                </tr>
                <tr>
                  <td>Bijkopen nodig</td>
                  <td>✅</td>
                  <td>✅</td>
                  <td className="shop-tokens__col-unlimited">❌</td>
                </tr>
                <tr>
                  <td>Maandelijks opzegb.</td>
                  <td>❌</td>
                  <td>❌</td>
                  <td className="shop-tokens__col-unlimited">✅</td>
                </tr>
                <tr>
                  <td>Ondersteuning</td>
                  <td>Basis</td>
                  <td>Basis</td>
                  <td className="shop-tokens__col-unlimited">Prioriteit</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {confirmCancel && (
        <div
          className="shop-tokens__modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Abonnement opzeggen"
        >
          <div className="shop-tokens__modal">
            <h2 className="shop-tokens__modal-title">Abonnement opzeggen</h2>
            <p className="shop-tokens__modal-text">
              Weet je zeker dat je wilt opzeggen? Je abonnement blijft actief tot{' '}
              {formatNlDate(endOfBillingMonth())}. Daarna worden deals verrekend
              via je token saldo.
            </p>
            <div className="shop-tokens__modal-actions">
              <button
                type="button"
                className="shop-tokens__modal-confirm"
                onClick={handleConfirmCancel}
              >
                Ja, zeg op
              </button>
              <button
                type="button"
                className="shop-tokens__modal-cancel"
                onClick={() => setConfirmCancel(false)}
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}

      <ShopBottomNav />
    </div>
  );
}


